import { transport, BASE_PATH } from "@/src/config/apiConfig.js";
import WorkspaceService from "@/src/api/WorkspaceService.js";

export default {
  getBasePath() {
    return BASE_PATH;
  },

  isDefined(val) {
    return typeof val !== "undefined" && val !== null;
  },

  async createSequence(sequenceData) {
    try {
      let res = await transport.post(
        BASE_PATH + "/api/sequences",
        sequenceData,
      );
      return res.data;
    } catch (error) {
      console.error("Error creating sequence:", error);
      throw error;
    }
  },

  async updateSequenceStatus(sequenceId, status) {
    try {
      let res = await transport.post(
        BASE_PATH + `/api/sequences/${sequenceId}/update_sequence_status`,
        {
          status: status,
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error updating sequence status:", error);
      throw error;
    }
  },

  async fetchSequences(
    page,
    pageSize,
    searchQuery,
    statusFilters,
    dateFilter,
    tags,
  ) {
    try {
      let params = {
        page: page,
        page_size: pageSize,
        search: searchQuery,
        created_date: dateFilter,
      };

      if (statusFilters?.length > 0) {
        params.statuses = statusFilters;
      }

      if (tags?.length > 0) {
        params.tags = tags;
      }

      let res = await transport.get(BASE_PATH + "/api/sequences", {
        params: params,
      });
      return res.data;
    } catch (error) {
      console.error("Error fetching sequences:", error);
      throw error;
    }
  },

  async fetchAllSteps() {
    try {
      let res = await transport.get(BASE_PATH + "/api/steps");
      return res.data;
    } catch (error) {
      console.error("Error fetching all steps:", error);
      throw error;
    }
  },

  async fetchValidSequences() {
    try {
      let res = await transport.get(BASE_PATH + "/api/usable_sequences", {});
      return res.data;
    } catch (error) {
      console.error("Error fetching valid sequences:", error);
      throw error;
    }
  },

  async fetchAssociatedSequences(entityType, entityId) {
    try {
      let res = await transport.get(
        `${BASE_PATH}/api/associated_sequences/${entityType}/${entityId}`,
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching associated sequences:", error);
      throw error;
    }
  },

  async getTagsForSequenceFilters() {
    try {
      let res = await transport.get(
        BASE_PATH + "/api/sequence_tags/fetch_tags",
        {},
      );
      return res.data;
    } catch (error) {
      console.error("Error getting tags for sequence filters:", error);
      throw error;
    }
  },

  async addEntitiesToSequence(sequenceId, entity_ids, entity_type) {
    try {
      let res = await transport.post(
        BASE_PATH + `/api/sequences/${sequenceId}/add_entities_to_sequence`,
        {
          entity_ids: entity_ids,
          entity_type: entity_type,
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error adding entities to sequence:", error);
      throw error;
    }
  },

  async addDifferentEntitiesToSequence(sequenceId, entity_data) {
    try {
      let res = await transport.post(
        BASE_PATH +
          `/api/sequences/${sequenceId}/add_different_entities_to_sequence`,
        entity_data,
      );
      return res.data;
    } catch (error) {
      console.error("Error adding different entities to sequence:", error);
      throw error;
    }
  },

  async removeEntitiesToSequence(sequenceId, entity_id, entity_type) {
    try {
      let res = await transport.post(
        BASE_PATH + `/api/sequences/${sequenceId}/remove_from_sequence`,
        { entity_id: entity_id, entity_type: entity_type },
      );
      return res.data;
    } catch (error) {
      console.error("Error removing entities from sequence:", error);
      throw error;
    }
  },

  async pauseEntityInSequence(sequenceId, entity_id, entity_type) {
    let res = await transport.post(
      BASE_PATH + `/api/sequences/${sequenceId}/pause_entity`,
      { entity_id: entity_id, entity_type: entity_type },
    );
    return res.data;
  },

  async resumeEntityInSequence(sequenceId, entity_id, entity_type) {
    let res = await transport.post(
      BASE_PATH + `/api/sequences/${sequenceId}/resume_entity`,
      { entity_id: entity_id, entity_type: entity_type },
    );
    return res.data;
  },

  async updateSequence(sequenceId, sequenceData) {
    try {
      let res = await transport.put(
        BASE_PATH + `/api/sequences/${sequenceId}`,
        sequenceData,
      );
      return res.data;
    } catch (error) {
      console.error("Error updating sequence:", error);
      throw error;
    }
  },

  async deleteSequence(sequenceId) {
    try {
      let res = await transport.post(
        BASE_PATH + `/api/sequences/${sequenceId}/delete`,
        {},
      );
      return res.data;
    } catch (error) {
      console.error("Error deleting sequence:", error);
      throw error;
    }
  },

  async deleteSequenceStep(sequenceId, sequenceStepId) {
    try {
      let res = await transport.post(
        BASE_PATH + `/api/sequences/${sequenceId}/delete_step`,
        {
          step_id: sequenceStepId,
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error deleting sequence step:", error);
      throw error;
    }
  },

  async updateSequenceStepStatus(sequenceId, stepVariantId, is_active) {
    try {
      let res = await transport.post(
        BASE_PATH + `/api/sequences/${sequenceId}/update_variant_status`,
        {
          variant_id: stepVariantId,
          is_active: is_active,
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error updating sequence step status:", error);
      throw error;
    }
  },

  async updateSequenceStep(sequenceId, formData) {
    try {
      let res = await transport.post(
        BASE_PATH + `/api/sequences/${sequenceId}/update_step`,
        formData,
      );
      return res.data;
    } catch (error) {
      console.error("Error updating sequence step:", error);
      throw error;
    }
  },

  async deleteSequenceStepVariant(sequenceId, stepVariantId) {
    try {
      let res = await transport.post(
        BASE_PATH + `/api/sequences/${sequenceId}/delete_variant`,
        {
          variant_id: stepVariantId,
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error deleting sequence step variant:", error);
      throw error;
    }
  },

  async fetchSequenceDetails(sequenceId) {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/sequences/${sequenceId}`,
        {
          params: {},
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching sequence details:", error);
      throw error;
    }
  },

  async fetchSequenceEmailAnalyticsData(sequenceId, params) {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/sequences/${sequenceId}/email_analytics`,
        {
          params: params,
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching sequence email analytics data:", error);
      throw error;
    }
  },

  //
  // async getSequenceEmailChartData(sequenceId) {
  //     let res = await transport.get(
  //         BASE_PATH + `/api/sequences/${sequenceId}/email_chart_data`,
  //         {
  //             params: {},
  //         },
  //     );
  //     return res.data;
  // },

  async fetchSequenceLinkedinAnalyticsData(sequenceId, params) {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/sequences/${sequenceId}/linkedin_analytics`,
        {
          params: params,
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching sequence LinkedIn analytics data:", error);
      throw error;
    }
  },

  // async getSequenceLinkedinChartData(sequenceId) {
  //     let res = await transport.get(
  //         BASE_PATH + `/api/sequences/${sequenceId}/linkedin_chart_data`,
  //         {
  //             params: {},
  //         },
  //     );
  //     return res.data;
  // },

  async fetchSequenceTaskAnalyticsData(sequenceId, params) {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/sequences/${sequenceId}/task_analytics`,
        {
          params: params,
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching sequence task analytics data:", error);
      throw error;
    }
  },

  // async getSequenceTaskChartData(sequenceId) {
  //     let res = await transport.get(
  //         BASE_PATH + `/api/sequences/${sequenceId}/task_chart_data`,
  //         {
  //             params: {},
  //         },
  //     );
  //     return res.data;
  // },

  async fetchSequenceLeadsAndContacts(
    sequenceId,
    page,
    page_size,
    statusFilter,
    searchQuery,
  ) {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/sequences/${sequenceId}/contacts`,
        {
          params: {
            page: page,
            page_size: page_size,
            status: statusFilter,
            search: searchQuery,
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching sequence leads and contacts:", error);
      throw error;
    }
  },

  async fetchSequenceSettings(sequenceId) {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/sequences/${sequenceId}/fetch_settings`,
        {
          params: {},
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching sequence settings:", error);
      throw error;
    }
  },

  async saveSequenceSettings(sequenceId, settings) {
    try {
      let res = await transport.post(
        BASE_PATH + `/api/sequences/${sequenceId}/save_settings`,
        {
          settings: settings,
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error saving sequence settings:", error);
      throw error;
    }
  },

  async fetchSequenceSteps(sequenceId) {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/sequences/${sequenceId}/fetch_steps`,
        {
          params: {},
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching sequence steps:", error);
      throw error;
    }
  },

  async fetchSequenceVariant(sequenceId, variantId) {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/sequences/${sequenceId}/fetch_variant`,
        {
          params: { variant_id: variantId },
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching sequence variant:", error);
      throw error;
    }
  },

  async addStepVariant(sequenceId, stepId, variant) {
    try {
      let res = await transport.post(
        BASE_PATH + `/api/sequences/${sequenceId}/add_variant`,
        {
          step_id: stepId,
          variant: variant,
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error adding step variant:", error);
      throw error;
    }
  },

  async moveStepUp(sequenceId, stepId) {
    try {
      let res = await transport.post(
        BASE_PATH + `/api/sequences/${sequenceId}/move_step_up`,
        {
          step_id: stepId,
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error moving step up:", error);
      throw error;
    }
  },

  async moveStepDown(sequenceId, stepId) {
    try {
      let res = await transport.post(
        BASE_PATH + `/api/sequences/${sequenceId}/move_step_down`,
        {
          step_id: stepId,
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error moving step down:", error);
      throw error;
    }
  },

  async saveStepVariant(sequenceId, variantId, variant, step_params) {
    try {
      let res = await transport.post(
        BASE_PATH + `/api/sequences/${sequenceId}/save_variant`,
        {
          variant_id: variantId,
          variant: variant,
          step: step_params,
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error saving step variant:", error);
      throw error;
    }
  },

  async createSequenceStep(sequenceId, stepData) {
    try {
      let res = await transport.post(
        BASE_PATH + `/api/sequences/${sequenceId}/add_step`,
        {
          step_data: stepData,
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error creating sequence step:", error);
      throw error;
    }
  },

  async searchPeople(search_query) {
    try {
      let params = {};

      if (search_query) {
        params.search_query = search_query;
      }

      let res = await transport.get(BASE_PATH + "/api/search_people", {
        params,
      });
      return res.data;
    } catch (error) {
      console.error("Error searching people:", error);
      throw error;
    }
  },

  async cloneSequence(sequenceId) {
    try {
      let res = await transport.post(
        BASE_PATH + `/api/sequences/${sequenceId}/clone`,
      );
      return res.data;
    } catch (error) {
      console.error("Error cloning sequence:", error);
      throw error;
    }
  },

  async fetchTemplateCategories() {
    try {
      let res = await transport.get(
        BASE_PATH + "/api/sequence_templates/categories",
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching template categories:", error);
      throw error;
    }
  },

  async fetchTemplates(params) {
    try {
      let res = await transport.get(
        BASE_PATH + "/api/sequence_templates/templates",
        {
          params: params,
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching templates:", error);
      throw error;
    }
  },

  async fetchTemplatesAdmin(params) {
    try {
      let res = await transport.get(
        BASE_PATH + "/api/sequence_templates/templates_admin",
        {
          params: params,
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching templates admin:", error);
      throw error;
    }
  },

  async createSequenceTemplate(templateData) {
    try {
      let res = await transport.post(
        BASE_PATH + "/api/sequence_templates/templates",
        templateData,
      );
      return res.data;
    } catch (error) {
      console.error("Error creating sequence template:", error);
      throw error;
    }
  },

  async fetchTemplateDetails(templateId) {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/sequence_templates/templates/${templateId}`,
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching template details:", error);
      throw error;
    }
  },

  async useTemplate(templateId, sequenceData) {
    try {
      const res = await transport.post(
        `${BASE_PATH}/api/sequence_templates/use_template/${templateId}`,
        sequenceData,
      );
      return res.data;
    } catch (error) {
      console.error("Error using template:", error);
      throw error;
    }
  },

  async updateTemplate(templateId, templateData) {
    try {
      let res = await transport.put(
        BASE_PATH + `/api/sequence_templates/templates/${templateId}`,
        templateData,
      );
      return res.data;
    } catch (error) {
      console.error("Error updating template:", error);
      throw error;
    }
  },

  async deleteTemplate(templateId) {
    try {
      let res = await transport.delete(
        BASE_PATH + `/api/sequence_templates/templates/${templateId}`,
      );
      return res.data;
    } catch (error) {
      console.error("Error deleting template:", error);
      throw error;
    }
  },

  async generatePreviewEmail(id, payload) {
    try {
      let res = await transport.post(
        BASE_PATH + `/api/sequences/${id}/generate_preview_email`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("Error generating preview email:", error);
      throw error;
    }
  },

  async generatePreviewLinkedin(id, payload) {
    try {
      let res = await transport.post(
        BASE_PATH + `/api/sequences/${id}/generate_preview_linkedin`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("Error generating preview LinkedIn:", error);
      throw error;
    }
  },

  async fetchFilteredSequences(viewId, page, pageSize, searchQuery) {
    try {
      const res = await transport.get(
        `${BASE_PATH}/api/outreach/views/${viewId}/sequences`,
        {
          params: {
            page_size: pageSize,
            page: page,
            search: searchQuery,
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching filtered sequences:", error);
      throw error;
    }
  },

  async fetchFilteredContactsAndLeads({
    viewId,
    sequenceId,
    page,
    pageSize,
    searchQuery,
  } = {}) {
    try {
      const res = await transport.get(
        `${BASE_PATH}/api/outreach/views/${viewId}/sequences/${sequenceId}/contacts`,
        {
          params: {
            page_size: pageSize,
            page: page,
            search: searchQuery,
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching filtered contacts and leads:", error);
      throw error;
    }
  },

  async fetchSequenceContactDetails(
    sequenceId,
    entityId,
    entityType,
    executionId,
  ) {
    try {
      const res = await transport.get(
        `${BASE_PATH}/api/sequences/${sequenceId}/contact-details`,
        {
          params: {
            entity_id: entityId,
            entity_type: entityType,
            execution_id: executionId,
            _t: Date.now(), // Cache-busting parameter
          },
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching sequence contact details:", error);
      throw error;
    }
  },

  async fetchSequenceEntityViews(id) {
    try {
      const res = await transport.get(
        `${BASE_PATH}/api/outreach/sequence_entity/views/${id}`,
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching sequence entity views:", error);
      throw error;
    }
  },

  async fetchAIVariables(
    page = 1,
    limit = 10,
    variableType = "user_generated",
  ) {
    try {
      let res = await transport.get(BASE_PATH + "/api/ai_variables", {
        params: {
          page,
          limit,
          variable_type: variableType,
        },
      });
      return res.data;
    } catch (error) {
      console.error("Error fetching AI variables:", error);
      throw error;
    }
  },

  async fetchAIVariable(id) {
    try {
      let res = await transport.get(BASE_PATH + `/api/ai_variables/${id}`);
      return res.data;
    } catch (error) {
      console.error("Error fetching AI variable:", error);
      throw error;
    }
  },

  async searchAIVariables(query, page = 1, limit = 10) {
    try {
      let res = await transport.get(BASE_PATH + "/api/ai_variables/search", {
        params: {
          q: query,
          page,
          limit,
        },
      });
      return res.data;
    } catch (error) {
      console.error("Error searching AI variables:", error);
      throw error;
    }
  },

  async createAIVariable(variableData) {
    try {
      let res = await transport.post(BASE_PATH + "/api/ai_variables", {
        ai_variable: variableData,
      });
      return res.data;
    } catch (error) {
      console.error("Error creating AI variable:", error);
      throw error;
    }
  },

  async updateAIVariable(id, variableData) {
    try {
      let res = await transport.put(BASE_PATH + `/api/ai_variables/${id}`, {
        ai_variable: variableData,
      });
      return res.data;
    } catch (error) {
      console.error("Error updating AI variable:", error);
      throw error;
    }
  },

  async deleteAIVariable(id) {
    try {
      let res = await transport.delete(BASE_PATH + `/api/ai_variables/${id}`);
      return res.data;
    } catch (error) {
      console.error("Error deleting AI variable:", error);
      throw error;
    }
  },

  async getAIVariablePreview({ id, sequence_id, entity_id, entity_type }) {
    try {
      let res = await transport.post(
        BASE_PATH + `/api/ai_variable/${id}/preview`,
        {
          sequence_id,
          entity_id,
          entity_type,
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error getting AI variable preview:", error);
      throw error;
    }
  },

  async adminUpdateAIVariable(id, variableData) {
    try {
      let res = await transport.post(
        BASE_PATH + `/api/ai_variable/${id}/admin_update`,
        {
          ai_variable: variableData,
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error admin updating AI variable:", error);
      throw error;
    }
  },

  async adminDeleteAIVariable(id) {
    try {
      let res = await transport.delete(
        BASE_PATH + `/api/ai_variable/${id}/admin_destroy`,
      );
      return res.data;
    } catch (error) {
      console.error("Error admin deleting AI variable:", error);
      throw error;
    }
  },

  // <-------------- New sequence flow api's --------------->

  async saveSequenceEntityConfig(sequenceId, configData) {
    try {
      const response = await transport.post(
        `${BASE_PATH}/api/sequence/${sequenceId}/entity/config`,
        configData,
      );
      return response.data;
    } catch (error) {
      console.error("Error saving sequence entity config:", error);
      throw error;
    }
  },

  async getUsersIntegrations(params = {}) {
    try {
      const response = await transport.get(
        `${BASE_PATH}/api/users/integrations`,
        { params },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching users integrations:", error);
      throw error;
    }
  },

  async getTeamsPersonalMailboxes(teamIds) {
    try {
      const response = await transport.get(
        `${BASE_PATH}/api/user/teams/personal/mailboxes`,
        {
          params: { team_ids: teamIds },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching teams personal mailboxes:", error);
      throw error;
    }
  },

  async validateEntities(validationData) {
    try {
      const sequenceId = validationData.sequence_id;
      let res = await transport.post(
        BASE_PATH + `/api/sequences/${sequenceId}/validate/entities`,
        validationData,
      );
      return res.data;
    } catch (error) {
      console.error("Error validating entities:", error);
      throw error;
    }
  },

  async removeMultipleEntitiesFromSequence(sequenceId, payload) {
    try {
      let res = await transport.post(
        BASE_PATH + `/api/sequences/${sequenceId}/bulk_remove_from_sequence`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("Error removing multiple entities from sequence:", error);
      throw error;
    }
  },

  async countRemovableEntities(sequenceId, unselectedIds = []) {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/sequences/${sequenceId}/count_removable_entities`,
        {
          params: {
            unselected_ids: unselectedIds,
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error counting removable entities:", error);
      throw error;
    }
  },

  async fetchSequenceEmailAccounts(sequenceId) {
    try {
      let res = await transport.get(
        BASE_PATH + `/api/sequences/email_accounts`,
        {
          params: {
            sequence_id: sequenceId,
          },
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching sequence email accounts:", error);
      throw error;
    }
  },
};
